import React, { FunctionComponent, useEffect, useState } from 'react';
import Select, { ThemeConfig } from 'react-select';
import { SelectOption, UserRoles, isCWInsightsRole } from '../../Shared/SharedModels';
import { User, UserRole } from '../TeamMemberModels';

import ApiHelper from '../../Shared/ApiHelper';
import { Form } from 'react-bootstrap';
import { useFormContext } from '../../Shared/contexts/FormProvider';
import { useMsal } from '@azure/msal-react';

interface RoleSelectProps {
    formTheme: ThemeConfig;
    editEnabledForAdmin: boolean;
    isCwInsightsCustomer: boolean;
}

const RoleSelect: FunctionComponent<RoleSelectProps> = ({ formTheme, editEnabledForAdmin, isCwInsightsCustomer }) => {
    const {
        errors,
        formState: { role },
        register,
        setValues,
        validateForm,
    } = useFormContext<User>();

    const hasError = !!errors?.role;

    const [roleOptions, setRoleOptions] = useState<SelectOption<UserRole>[]>([
        { label: 'Member', value: { description: 'Member', name: 'Member', roleKey: 40 } },
    ]);

    const [api] = useState(new ApiHelper());
    const { instance } = useMsal();

    useEffect(() => {
        api.callApi(
            instance,
            [process.env.REACT_APP_B2C_SCOPE ?? ''],
            process.env.REACT_APP_NET_API_URL + '/User/roles'
        ).then(async (result: Response) => {
            const roles: UserRole[] = await result.json();

            // Filter roles based on customer type to prevent validation mismatches
            const filteredRoles = roles.filter((role: UserRole) => {
                const isInsightsRole = isCWInsightsRole(role.roleKey);
                // CW Insights customers should only see CW Insights roles (100+)
                // Standard customers should only see standard roles (1-99)
                return isCwInsightsCustomer ? isInsightsRole : !isInsightsRole;
            });

            const mappedRoles = filteredRoles
                .sort((roleA: UserRole, roleB: UserRole) => roleA.roleKey - roleB.roleKey)
                .map((role: UserRole) => {
                    return {
                        label: role.name,
                        value: role,
                    } as SelectOption<UserRole>;
                });
            setRoleOptions(mappedRoles);
        });
    }, [api, instance, isCwInsightsCustomer]);

    return (
        <Form.Group className="mb-4">
            <Form.Label className={(!editEnabledForAdmin) ? 'disabled-label' : ''}>Role</Form.Label>

            <Select
                {...register('role')}
                options={roleOptions}
                value={role ? [{ label: role?.name, value: role }] : undefined}
                theme={formTheme}
                aria-invalid={!role}
                placeholder="Select Role..."
                isDisabled={!editEnabledForAdmin}
                onChange={(selectedOption) => {
                    if (selectedOption) {
                        const newRoleKey = selectedOption.value?.roleKey;

                        setValues((prev) => {
                            // CW Insights roles don't use org/project/source system assignments
                            if (isCWInsightsRole(newRoleKey)) {
                                return {
                                    ...prev,
                                    role: selectedOption.value,
                                    roleKey: selectedOption.value.roleKey,
                                    sourceSystems: [],
                                    organizations: [],
                                    projects: [],
                                };
                            }

                            // Standard ClientInsight role logic
                            return {
                                ...prev,
                                role: selectedOption.value,
                                roleKey: selectedOption.value.roleKey,
                                sourceSystems:
                                    newRoleKey === UserRoles['Global Admin'] ||
                                    newRoleKey === UserRoles['Org Admin'] ||
                                    newRoleKey === UserRoles['Member']
                                        ? []
                                        : prev.sourceSystems,
                                organizations:
                                    newRoleKey === UserRoles['Global Admin'] ||
                                    newRoleKey === UserRoles['Source System Admin']
                                        ? []
                                        : prev.organizations,
                                projects: newRoleKey === UserRoles['Project User'] ? prev.projects : [],
                            };
                        });
                    }
                }}
                onBlur={validateForm}
                className={hasError ? 'is-invalid' : ''}
                classNamePrefix="base-select"
                menuPlacement="auto"
            />
            <Form.Control.Feedback type="invalid">{errors?.role}</Form.Control.Feedback>
        </Form.Group>
    );
};

export default RoleSelect;
